<template>
    <div class="auth__resetpassword_form">
        <div class="container">
            <div class="columns is-variable">
                <div class="column">
                    <div class="auth__resetpassword_form_box">
                        <div class="auth__resetpassword_form_box_title">{{ $t('auth.resetPassword.title.reset') }}</div>
                        <div class="auth__resetpassword_form_box_description">{{ boxDescription }}</div>
                        <div class="auth__resetpassword_form_box_fields">
                            <form-text
                                v-show="emailStage && !codeStage && !passwordStage"
                                :disabled="Boolean(routeQueryForceResetPassword)"
                                :label="$t('auth.login.labels.email')"
                                :name="'resetpassword-email'"
                                :v-validate="'required|email'"
                                :data-vv-as="'email'"
                                @emit-enter="resetpasswordSendCode"
                                v-model="email"
                            />
                            <form-checkbox
                                v-show="emailStage && !codeStage && !passwordStage"
                                :label="'Already have a verification code?'"
                                :name="'resetpassword-havecode'"
                                v-model="alreadyHasACode"
                            />
                            <form-text
                                v-show="!emailStage && codeStage && !passwordStage"
                                :label="$t('auth.resetPassword.labels.verification')"
                                :name="'resetpassword-verificationcode'"
                                :v-validate="{ required: true, regex: /^[0-9]*$/, length: 6 }"
                                :data-vv-as="'verification code'"
                                @emit-enter="resetpasswordSetCode"
                                v-model="code"
                            />
                            <form-text
                                v-show="!emailStage && !codeStage && passwordStage"
                                :password="true"
                                :label="$t('auth.resetPassword.labels.newPassword')"
                                :name="'resetpassword-newpassword'"
                                :v-validate="{ required: true, regex: passwordRegex }"
                                :data-vv-as="'new password'"
                                @emit-enter="resetpasswordConfirmNewPassword"
                                v-model="newPassword"
                            >Password length must be at least 8 and includes:<br>- lowercase letter<br>- uppercase letter<br>- special character<br>- number
                            </form-text>
                            <form-text
                                v-show="!emailStage && !codeStage && passwordStage"
                                :password="true"
                                :label="$t('auth.resetPassword.labels.confirmPassword')"
                                :name="'resetpassword-confirmnewpassword'"
                                :v-validate="{ required: true, regex: passwordRegex, is: newPassword }"
                                :data-vv-as="'confirm new password'"
                                @emit-enter="resetpasswordConfirmNewPassword"
                                v-model="newPasswordConfirm"
                            ></form-text>
                        </div>
                        <transition name="fade" mode="out-in">
                            <template v-if="error">
                                <div class="auth__resetpassword_form_box_error">
                                    <component-note :danger="true">{{ error }}</component-note>
                                </div>
                            </template>
                        </transition>
                        <div class="auth__resetpassword_form_box_actions">
                            <div class="columns is-variable">
                                <div class="column is-6">
                                    <component-button
                                        :theme="'default'"
                                        :size="'full'"
                                        :icon="true"
                                        :icon-position="'left'"
                                        :icon-name="'arrow-left'"
                                        :to="{ name: 'auth-login' }"
                                        :name="'auth-resetpassword-form-box-actions-back'"
                                    > {{ $t('auth.resetPassword.buttons.back') }}
                                    </component-button>
                                </div>
                                <div class="column is-6">
                                    <component-button
                                        v-if="emailStage && !codeStage && !passwordStage"
                                        :theme="'primary'"
                                        :size="'full'"
                                        :animate-loading="true"
                                        :animate-loading-event-name="'resetpassword-send-code'"
                                        @click="goToCodeStage"
                                        :name="'auth-resetpassword-form-box-actions-send'"
                                    >{{ alreadyHasACode ? 'Next step' : $t('auth.resetPassword.buttons.send') }}
                                    </component-button>
                                    <component-button
                                        v-if="!emailStage && codeStage && !passwordStage"
                                        :theme="'primary'"
                                        :size="'full'"
                                        @click="goToPasswordStage"
                                        :name="'auth-resetpassword-form-box-actions-next'"
                                    >{{ $t('auth.resetPassword.buttons.next') }}
                                    </component-button>
                                    <component-button
                                        v-if="!emailStage && !codeStage && passwordStage"
                                        :theme="'primary'"
                                        :size="'full'"
                                        :animate-loading="true"
                                        :animate-loading-event-name="'resetpassword-confirm-password'"
                                        @click="resetpasswordConfirmNewPassword"
                                        :name="'auth-resetpassword-form-box-actions-confirm'"
                                    >{{ $t('auth.resetPassword.buttons.confirmPassword') }}
                                    </component-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { passwordRegex } from '@/variables'
import FormCheckbox from '@/components/default/forms/FormCheckbox'

export default {
  name: 'auth-resetpassword-form',
  components: { FormCheckbox, ComponentNote, ComponentButton, FormText },
  data () {
    return {
      emailStage: true,
      codeStage: false,
      passwordStage: false,
      alreadyHasACode: false,
      passwordRegex
    }
  },
  computed: {
    email: {
      get () {
        return this.$store.getters.getAuthUserResetPasswordEmail
      },
      set (value) {
        this.$store.dispatch('authUserSetResetPasswordEmail', value)
      }
    },
    code: {
      get () {
        return this.$store.getters.getAuthUserResetPasswordCode
      },
      set (value) {
        this.$store.dispatch('authUserSetResetPasswordCode', value)
      }
    },
    newPassword: {
      get () {
        return this.$store.getters.getAuthUserResetPasswordNewPassword
      },
      set (value) {
        this.$store.dispatch('authUserSetResetPasswordNewPassword', value)
      }
    },
    newPasswordConfirm: {
      get () {
        return this.$store.getters.getAuthUserResetPasswordNewPasswordConfirm
      },
      set (value) {
        this.$store.dispatch('authUserSetResetPasswordNewPasswordConfirm', value)
      }
    },
    error () {
      return this.$store.getters.getAuthUserResetPasswordError
    },
    //
    boxDescription () {
      return this.routeQueryForceResetPassword ? 'Please check your inbox to find a verification code or generate new one to perform reset password.' : 'Enter the email address associated with your account, and we’ll email you a link to reset your password.'
    },
    routeQueryForceResetPassword () {
      return this.$route.query.forceResetPassword
    }
  },
  methods: {
    goToCodeStage () {
      this.$store.dispatch('authUserSetResetPasswordError', '')
      if (this.alreadyHasACode) {
        this.emailStage = false
        this.codeStage = true
        this.$eventBus.$emit('stop-resetpassword-send-code')
      } else {
        this.resetpasswordSendCode()
      }
    },
    goToPasswordStage () {
      this.resetpasswordSetCode()
    },
    resetpasswordSendCode () {
      this.$validator.validate('resetpassword-email').then((result) => {
        if (result) {
          this.$store.dispatch('authUserResetPasswordSendCode').then(() => {
            this.emailStage = false
            this.codeStage = true
          }).finally(() => {
            this.$eventBus.$emit('stop-resetpassword-send-code')
          })
        } else {
          this.$eventBus.$emit('stop-resetpassword-send-code')
        }
      })
    },
    resetpasswordSetCode () {
      this.$validator.validate('resetpassword-verificationcode').then((result) => {
        if (result) {
          this.codeStage = false
          this.passwordStage = true
        }
      })
    },
    resetpasswordConfirmNewPassword () {
      this.$validator.validate('resetpassword-newpassword').then((result) => {
        if (result) {
          this.$validator.validate('resetpassword-confirmnewpassword').then((result) => {
            if (result) {
              this.$store.dispatch('authUserResetPasswordConfirmNewPassword').then(() => {
                this.emailStage = true
                this.codeStage = false
                this.passwordStage = false
              }).catch(() => {
                this.alreadyHasACode = false
                this.emailStage = true
                this.codeStage = false
                this.passwordStage = false
                this.$store.dispatch('authUserSetResetPasswordCode', '')
                this.$store.dispatch('authUserSetResetPasswordNewPassword', '')
              }).finally(() => {
                this.$eventBus.$emit('stop-resetpassword-confirm-password')
                this.$validator.reset()
              })
            } else {
              this.$eventBus.$emit('stop-resetpassword-confirm-password')
            }
          })
        } else {
          this.$eventBus.$emit('stop-resetpassword-confirm-password')
        }
      })
    }
  },
  mounted () {
    if (!this.email && this.routeQueryForceResetPassword) {
      this.$store.dispatch('authUserSetResetPasswordEmail', this.routeQueryForceResetPassword)
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/auth/resetpassword/authresetpasswordform";
</style>
